// External libraries
import React from 'react';

// Internal modules
import { CardsWrapper, Container, List, Separator } from './StyledComponents';
import { WalletsListLoading } from './WalletsListLoading';
import { WalletsFooter } from './WalletsFooter';
import { WalletRow } from './WalletRow';
import { SingularWalletOption } from './SingularWalletOption';
import { MultipleWalletOptions } from './MultipleWalletOptions';
import { isVenmoOption } from './utils';
import type { CheckoutOption } from './types';

export type { CheckoutOption } from './types';

export type WalletsListProps = {
  isLoading: boolean;
  checkoutOptions: CheckoutOption[] | null;
  connectedByLabel: string;
  ppwWalletsLabel: string;
  ppwWalletsDescription: string;
  ppwLogoUrl: string;
  walletsListLabel?: string;
  onWalletClick: (wallet: CheckoutOption) => void;
};

export const WalletsList = ({
  isLoading,
  checkoutOptions,
  connectedByLabel,
  ppwWalletsLabel,
  ppwWalletsDescription,
  ppwLogoUrl,
  walletsListLabel,
  onWalletClick,
}: WalletsListProps): React.ReactElement | null => {
  if (isLoading) {
    return <WalletsListLoading />;
  }

  if (checkoutOptions === null || checkoutOptions.length === 0) {
    return null;
  }

  const venmoOption = checkoutOptions.find(isVenmoOption);
  const ppwOptions = checkoutOptions.filter(option => !isVenmoOption(option));

  if (!venmoOption && ppwOptions.length === 0) {
    return null;
  }

  const footer = (
    <WalletsFooter
      connectedByLabel={connectedByLabel}
      ppwLogoUrl={ppwLogoUrl}
    />
  );

  return (
    <CardsWrapper>
      <Container
        data-testid="wallets-list"
        role={walletsListLabel ? 'region' : undefined}
        aria-label={walletsListLabel}
      >
        <List>
          {venmoOption && (
            <li>
              <WalletRow wallet={venmoOption} onWalletClick={onWalletClick} />
            </li>
          )}
          {venmoOption && ppwOptions.length > 0 && (
            <li>
              <Separator
                data-testid="wallets-list-separator"
                aria-hidden="true"
              />
            </li>
          )}
          {ppwOptions.length > 1 && (
            <MultipleWalletOptions
              options={ppwOptions}
              ppwWalletsLabel={ppwWalletsLabel}
              ppwWalletsDescription={ppwWalletsDescription}
              onWalletClick={onWalletClick}
              footer={footer}
            />
          )}
          {ppwOptions.length === 1 && (
            <SingularWalletOption
              wallet={ppwOptions[0]}
              onWalletClick={onWalletClick}
              footer={footer}
            />
          )}
          {ppwOptions.length === 0 && venmoOption && <li>{footer}</li>}
        </List>
      </Container>
    </CardsWrapper>
  );
};
